import { RightArrowIcon } from "assets/icons";
import { toastServerError } from "helpers";
import { useGetAllCategories } from "modules/categories";
import { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, Select } from "ui";

import { useAddAffinityRule } from "../../apis";
import { AFFINITY_MAX_WEIGHT, AFFINITY_MIN_WEIGHT } from "../../constants";
import {
  buildCategoryOptions,
  flattenCategoryTree,
  leafCategories
} from "../../helpers";

import styles from "./AffinityAddForm.module.scss";

const WEIGHT_OPTIONS = Array.from(
  { length: AFFINITY_MAX_WEIGHT - AFFINITY_MIN_WEIGHT + 1 },
  (_, index) => {
    const weight = AFFINITY_MIN_WEIGHT + index;
    return { label: `${weight}`, value: `${weight}` };
  }
);

const AffinityAddForm = () => {
  const { t } = useTranslation();

  const { categories } = useGetAllCategories();
  // Leaves only: a product carries a sub-sub-category id and the resolver looks
  // the rule up by that id without walking up the tree, so a rule written
  // against a parent category can never fire.
  const categoryOptions = useMemo(
    () =>
      buildCategoryOptions(
        leafCategories(categories),
        flattenCategoryTree(categories)
      ),
    [categories]
  );

  const [fromCategoryId, setFromCategoryId] = useState<string | null>(null);
  const [toCategoryId, setToCategoryId] = useState<string | null>(null);
  const [weight, setWeight] = useState("1");

  const { mutate: addRule, isLoading } = useAddAffinityRule({
    onSuccess: () => {
      toast.success(t("Affinity rule added"));
      setFromCategoryId(null);
      setToCategoryId(null);
      setWeight("1");
    },
    onError: toastServerError
  });

  const handleAdd = () => {
    if (!fromCategoryId || !toCategoryId) {
      toast.error(t("Pick both categories"));
      return;
    }

    if (fromCategoryId === toCategoryId) {
      toast.error(t("A category cannot pair with itself"));
      return;
    }

    addRule({
      fromCategoryId,
      toCategoryId,
      weight: Number(weight)
    });
  };

  return (
    <div className={styles.form}>
      <div className={styles.form__field}>
        <Select
          label={t("From category")}
          placeholder={t("Pick a category")}
          options={categoryOptions}
          value={fromCategoryId}
          withFilterInput
          onChange={(selected) => setFromCategoryId(selected as string)}
        />
      </div>

      <span className={styles.form__arrow}>
        <RightArrowIcon width={20} height={20} />
      </span>

      <div className={styles.form__field}>
        <Select
          label={t("Goes with (to category)")}
          placeholder={t("Pick a category")}
          options={categoryOptions}
          value={toCategoryId}
          withFilterInput
          onChange={(selected) => setToCategoryId(selected as string)}
        />
      </div>

      <div className={styles.form__weight}>
        <Select
          label={t("Weight")}
          options={WEIGHT_OPTIONS}
          value={weight}
          onChange={(selected) => setWeight(selected as string)}
        />
      </div>

      <Button
        className={styles.form__submit}
        loading={isLoading}
        onClick={handleAdd}
      >
        {t("Add rule")}
      </Button>
    </div>
  );
};

export default AffinityAddForm;
